import React, { useMemo, useRef, useState } from "react";
import cn from "classnames";
import { useAtom, useAtomValue } from "jotai";
import { useQuery, useQueryClient } from "react-query";
import { Icon } from "assets/icons";
import { getErrorMessage } from "services/api";
import {
  createProduct,
  updateProduct,
  uploadProductImage,
  applyImageLinkToProduct,
  getProductImages,
  deleteProductImage,
  getNds,
} from "features/Products/api";
import { useMeasureUnits } from "shared-apis";
import { settingsAtom } from "atoms/settingsAtom";
import { activeItemsGroupAtom, selectedProductAtom } from "atoms/catalogAtom";
import { resolveImageUrl } from "config/env";

import {
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useModal,
} from "components/Modal";
import { Button } from "components/Button";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { Checkbox } from "components/Checkbox";
import { Accordion } from "components/Accordion";
import { RegularText, MediumText } from "components/Text";
import { VerticalSpace } from "components/Spacing";

import { emptyPhoto } from "utils/assets";

import { buildGroupOptions } from "./groupOptions";
import styles from "./styles.module.scss";

const getImageDisplayUrl = (link) => {
  if (!link) return emptyPhoto;
  return resolveImageUrl(link);
};

const toImagePath = (link) => {
  try {
    return new URL(link).pathname;
  } catch {
    return link;
  }
};

const flagToNumber = (val) => (val ? 1 : 0);

/** Свойства / создание товара */
export const ProductModal = ({ isEdit, groups }) => {
  const queryClient = useQueryClient();
  const { closeModal } = useModal();

  const settings = useAtomValue(settingsAtom);
  const [activeGroup] = useAtom(activeItemsGroupAtom);
  const [selectedProduct, setSelectedProduct] = useAtom(selectedProductAtom);

  const product = isEdit ? selectedProduct : null;

  /* ------- справочники ------- */
  const measureUnitsQuery = useMeasureUnits();
  const ndsQuery = useQuery(["nds"], getNds);

  const measureOptions = useMemo(
    () =>
      (measureUnitsQuery.data?.items ?? []).map((m) => ({
        value: String(m.measureid),
        label: m.name,
      })),
    [measureUnitsQuery.data]
  );

  const ndsOptions = useMemo(
    () =>
      (ndsQuery.data?.items ?? []).map((n) => ({
        value: String(n.ndsid),
        label: String(n.value) === "0" ? n.name : String(n.value),
      })),
    [ndsQuery.data]
  );

  const groupOptions = useMemo(() => buildGroupOptions(groups), [groups]);

  /* ------- state формы ------- */
  const defaultPrice = useMemo(() => {
    if (!product?.prices?.length) return "";
    const bySettings = product.prices.find(
      (p) => String(p.pricelistid) === String(settings?.pricelistid)
    );
    return String((bySettings ?? product.prices[0])?.price ?? "");
  }, [product, settings]);

  const [form, setForm] = useState(() => ({
    name: product?.name ?? "",
    code: product?.code ?? "",
    position: product?.position ?? "",
    siteimportcode: product?.siteimportcode ?? "",
    importcode: product?.importcode ?? "",
    price: defaultPrice,
    available: !!(product?.avaliable ?? 0),
    service: !!(product?.service ?? 0),
    nodiscount: !!(product?.nodiscount ?? 0),
    onlywhole: !!(product?.onlywhole ?? 0),
    upload: !!(product?.upload ?? 0),
    nobonus: !!(product?.nobonus ?? 0),
  }));

  const patch = (update) => setForm((prev) => ({ ...prev, ...update }));

  const [groupOption, setGroupOption] = useState(() => {
    const targetId = isEdit
      ? String(product?.itemgroupid ?? "0")
      : String(activeGroup?.itemgroupid ?? "0");
    return groupOptions.find((o) => o.value === targetId) || groupOptions[0];
  });

  const [measureOption, setMeasureOption] = useState(null);
  const [ndsOption, setNdsOption] = useState(null);

  // Дефолты селектов после загрузки справочников
  const measureInitRef = useRef(false);
  if (!measureInitRef.current && measureOptions.length) {
    measureInitRef.current = true;
    const targetId = isEdit ? product?.measureid : settings?.defaultmeasureid;
    setMeasureOption(
      measureOptions.find((o) => o.value === String(targetId)) || null
    );
  }
  const ndsInitRef = useRef(false);
  if (!ndsInitRef.current && ndsOptions.length) {
    ndsInitRef.current = true;
    const targetId = isEdit ? product?.ndsid : settings?.ndsid;
    setNdsOption(ndsOptions.find((o) => o.value === String(targetId)) || null);
  }

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  /* ------- фото ------- */
  const imagesQuery = useQuery(
    ["product-images", product?.itemid],
    () => getProductImages(Number(product.itemid)),
    { enabled: Boolean(isEdit && product?.itemid) }
  );
  const existingImages = imagesQuery.data?.items ?? [];

  const [uploadedImages, setUploadedImages] = useState([]);
  // selectedPhoto: { kind: "existing", id, link } | { kind: "uploaded", link }
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const data = await uploadProductImage(file);
      const link =
        typeof data === "string"
          ? data
          : data?.path || data?.file || data?.link;
      if (link) setUploadedImages((prev) => [...prev, link]);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const removeSelectedPhoto = async () => {
    if (!selectedPhoto || deletingPhoto) return;

    if (selectedPhoto.kind === "uploaded") {
      setUploadedImages((prev) => prev.filter((l) => l !== selectedPhoto.link));
      setSelectedPhoto(null);
      return;
    }

    // Уже привязанное к товару фото — удаляем на сервере
    setDeletingPhoto(true);
    setError("");
    try {
      await deleteProductImage(selectedPhoto.id);
      setSelectedPhoto(null);
      queryClient.invalidateQueries(["product-images", product?.itemid]);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setDeletingPhoto(false);
    }
  };

  const togglePhoto = (photo) =>
    setSelectedPhoto((prev) =>
      prev?.kind === photo.kind &&
      prev?.link === photo.link &&
      prev?.id === photo.id
        ? null
        : photo
    );

  /* ------- сохранение ------- */
  const [descriptionCollapsed, setDescriptionCollapsed] = useState(false);
  const [photosCollapsed, setPhotosCollapsed] = useState(true);

  const handleSave = async () => {
    if (!form.name || !form.price) {
      setError('Необходимо заполнить "Наименование" и "Цену"');
      return;
    }

    setSaving(true);
    setError("");

    const positionNumber = Number(form.position);
    const payload = {
      name: form.name,
      // 0 = корень («Все товары») — валидное значение
      itemgroupid: Number(groupOption?.value ?? 0),
      avaliable: flagToNumber(form.available),
      nodiscount: flagToNumber(form.nodiscount),
      service: flagToNumber(form.service),
      onlywhole: flagToNumber(form.onlywhole),
      nobonus: flagToNumber(form.nobonus),
      upload: flagToNumber(form.upload),
      measureid: measureOption?.value,
      code: form.code,
      importcode: form.importcode,
      siteimportcode: form.siteimportcode,
      position:
        form.position === "" || Number.isNaN(positionNumber)
          ? null
          : positionNumber,
      ndsid: Number(ndsOption?.value),
      price: Number(form.price),
      discription: "",
      deleted: 0,
    };

    try {
      let itemid = product?.itemid;
      if (isEdit) {
        await updateProduct({
          ...payload,
          itemid: product.itemid,
          pricelistid: settings?.pricelistid,
        });
      } else {
        const created = await createProduct(payload);
        itemid = created?.itemid ?? created?.data?.itemid;
      }

      if (itemid && uploadedImages.length) {
        await Promise.all(
          uploadedImages.map((link) =>
            applyImageLinkToProduct({ itemid, link: toImagePath(link) })
          )
        );
      }

      queryClient.invalidateQueries(["products"]);
      if (isEdit) setSelectedProduct(null);
      closeModal();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  /* ------- рендер ------- */
  return (
    <ModalShell width={940} className={styles.productModal}>
      <ModalHeader
        title={isEdit ? product?.name : "Создать новый продукт"}
        onClose={closeModal}
      />
      <ModalBody>
        <Accordion
          withIcon
          filled
          collapsed={descriptionCollapsed}
          handleCollapse={() => setDescriptionCollapsed((v) => !v)}
          parent={<MediumText size={16}>Описание</MediumText>}
        >
          <div className={styles.productFormGrid}>
            <div className={styles.productFormCol}>
              {isEdit && (
                <Input
                  label="ID"
                  labelWidth={120}
                  value={product?.itemid ?? ""}
                  disabled
                  inputWidth={180}
                />
              )}
              <Input
                label="*Наименование"
                labelWidth={120}
                value={form.name}
                onChange={(ev) => patch({ name: ev.target.value })}
                inputWidth={180}
              />
              <Input
                label="Артикул"
                labelWidth={120}
                value={form.code}
                onChange={(ev) => patch({ code: ev.target.value })}
                inputWidth={180}
              />
              <Input
                label="Номер позиции"
                labelWidth={120}
                type="number"
                value={form.position}
                onChange={(ev) => patch({ position: ev.target.value })}
                inputWidth={180}
              />
              <Input
                label="Штрихкод"
                labelWidth={120}
                value={form.siteimportcode}
                onChange={(ev) => patch({ siteimportcode: ev.target.value })}
                inputWidth={180}
              />
            </div>
            <div className={styles.productFormCol}>
              <Select
                label="В группе"
                labelWidth={110}
                options={groupOptions}
                value={groupOption}
                onChange={setGroupOption}
                width={200}
              />
              <Select
                label="Ед. изм."
                labelWidth={110}
                options={measureOptions}
                value={measureOption}
                onChange={setMeasureOption}
                isLoading={measureUnitsQuery.isLoading}
                width={200}
              />
              <Input
                label="*Цена (₽)"
                labelWidth={110}
                type="number"
                value={form.price}
                onChange={(ev) => patch({ price: ev.target.value })}
                inputWidth={200}
              />
              <Input
                label="Внешний код"
                labelWidth={110}
                value={form.importcode}
                onChange={(ev) => patch({ importcode: ev.target.value })}
                inputWidth={200}
              />
              <Select
                label="Ставка НДС"
                labelWidth={110}
                options={ndsOptions}
                value={ndsOption}
                onChange={setNdsOption}
                isLoading={ndsQuery.isLoading}
                width={200}
              />
            </div>
          </div>

          <VerticalSpace size={16} />

          <div className={styles.productCheckboxes}>
            <Checkbox
              label="Доступен в терминале"
              checked={form.available}
              onChange={(val) => patch({ available: val })}
            />
            <Checkbox
              label="Услуга"
              checked={form.service}
              onChange={(val) => patch({ service: val })}
            />
            <Checkbox
              label="Не участвует в скидках"
              checked={form.nodiscount}
              onChange={(val) => patch({ nodiscount: val })}
            />
            <Checkbox
              label="Продавать только целое количество"
              checked={form.onlywhole}
              onChange={(val) => patch({ onlywhole: val })}
            />
            <Checkbox
              label="Выгружать на сайт"
              checked={form.upload}
              onChange={(val) => patch({ upload: val })}
            />
            <Checkbox
              label="Нельзя оплатить бонусами"
              checked={form.nobonus}
              onChange={(val) => patch({ nobonus: val })}
            />
          </div>
        </Accordion>

        <VerticalSpace size={16} />

        <Accordion
          withIcon
          filled
          collapsed={photosCollapsed}
          handleCollapse={() => setPhotosCollapsed((v) => !v)}
          parent={<MediumText size={16}>Фото</MediumText>}
        >
          <div className={styles.photosGrid}>
            {existingImages.map((image) => (
              <button
                key={`existing-${image.itemimageid}`}
                type="button"
                className={cn(
                  styles.photoBox,
                  selectedPhoto?.kind === "existing" &&
                    selectedPhoto?.id === image.itemimageid &&
                    styles.photoBoxSelected
                )}
                onClick={() =>
                  togglePhoto({
                    kind: "existing",
                    id: image.itemimageid,
                    link: image.link,
                  })
                }
              >
                <img src={getImageDisplayUrl(image.link)} alt="" />
              </button>
            ))}
            {uploadedImages.map((link) => (
              <button
                key={link}
                type="button"
                className={cn(
                  styles.photoBox,
                  selectedPhoto?.kind === "uploaded" &&
                    selectedPhoto?.link === link &&
                    styles.photoBoxSelected
                )}
                onClick={() => togglePhoto({ kind: "uploaded", link })}
              >
                <img src={getImageDisplayUrl(link)} alt="" />
              </button>
            ))}
            <button
              type="button"
              className={styles.photoUploadBox}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <RegularText size={13} color="muted">
                  Загрузка...
                </RegularText>
              ) : (
                <Icon type="plus" size={24} />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={handleFileChange}
            />
          </div>
          <div className={styles.photosActions}>
            <Button
              icon="delete"
              onClick={removeSelectedPhoto}
              disabled={!selectedPhoto}
              loading={deletingPhoto}
              title="Удалить выбранное фото"
            />
          </div>
        </Accordion>
      </ModalBody>
      <ModalFooter>
        {!!error && (
          <RegularText size={13} color="danger" className={styles.footerError}>
            {error}
          </RegularText>
        )}
        <Button
          icon="galochka"
          variant="secondary"
          onClick={handleSave}
          loading={saving}
        >
          Сохранить
        </Button>
      </ModalFooter>
    </ModalShell>
  );
};
